import React, { forwardRef, useState } from "react";
import PropTypes from "prop-types";
import requiredIf from "react-required-if";
import cx from "classnames";

import { mapChildrenIndexedByType } from "../../helper/map-children";
import { useUniqueId } from "../hooks/useUniqueId";
import { A11yText } from "../A11yText";
import { ColorSelectionProvider } from "./ColorSelectionContext";
import ColorSelectionItem from "./ColorSelectionItem";

import styles from "./ColorSelection.scss";

export const ColorSelection = forwardRef((props, ref) => {
  const {
    value: initialValue,
    onChange: onColorChange,
    label,
    labelVisible,
    children,
    vertical,
    interactive,
    ...otherProps
  } = props;
  const [value, setValue] = useState(initialValue);
  const { id, name } = otherProps;
  const colorSelectionId =
    id || `color-selection-${useUniqueId("ColorSelection", props) + 1}`;
  const colorSelectionName = name || colorSelectionId;

  function onChange(newValue, event) {
    setValue(newValue);
    if (onColorChange) onColorChange(newValue, event);
  }

  return (
    <fieldset ref={ref} className={styles.colorSelection}>
      {labelVisible && interactive ? (
        <>
          <legend className={styles.legend}>
            {label}:<span className={styles.value}>{value}</span>
          </legend>
        </>
      ) : (
        <A11yText tag="legend">{label}</A11yText>
      )}

      <ul
        className={cx({
          [styles.items]: interactive,
          [styles.nonInteractiveItems]: !interactive,
          [styles.vertical]: vertical,
        })}
        {...otherProps}
      >
        {mapChildrenIndexedByType(children, (child, childIndexByType) => (
          <ColorSelectionProvider
            value={{
              id: colorSelectionId,
              name: colorSelectionName,
              itemId: childIndexByType + 1,
              value,
              onChange,
              interactive,
            }}
          >
            {child}
          </ColorSelectionProvider>
        ))}
      </ul>
    </fieldset>
  );
});

ColorSelection.propTypes = {
  value: requiredIf(PropTypes.string, (props) => props.interactive),
  label: requiredIf(PropTypes.string, (props) => props.interactive),
  labelVisible: PropTypes.bool,
  onChange: PropTypes.func,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  interactive: PropTypes.bool,
  vertical: PropTypes.bool,
};

ColorSelection.defaultProps = {
  value: "",
  label: "",
  onChange: null,
  labelVisible: false,
  interactive: true,
  vertical: false,
};

ColorSelection.Item = ColorSelectionItem;

ColorSelection.displayName = "ColorSelection";

export default ColorSelection;
